/**
 * Minimum gap between activity reports. The server marks a user Idle after 5 minutes without one,
 * so once a minute keeps an active user Online with plenty of slack while costing almost nothing.
 */
export const ACTIVITY_REPORT_INTERVAL_MS = 60_000;

export interface ActivityReporterTimers {
    now: () => number;
    setTimeout: (callback: () => void, ms: number) => unknown;
}

const defaultTimers: ActivityReporterTimers = {
    now: () => Date.now(),
    setTimeout: (callback, ms) => setTimeout(callback, ms),
};

/**
 * Returns an input handler that calls `send` at most once per `intervalMs`:
 * - leading edge: the first input after a quiet spell sends at once, so a user coming back from
 *   Idle turns Online on the viewer's next poll rather than a minute later;
 * - trailing edge: input that arrives while a window is still open sends once when it closes, so
 *   the server never judges a user by input older than what they actually did. Without it, a user
 *   who stops 50 seconds into a window would read Idle almost a minute early.
 */
export function createActivityReporter(
    send: () => void,
    intervalMs: number = ACTIVITY_REPORT_INTERVAL_MS,
    timers: ActivityReporterTimers = defaultTimers,
): () => void {
    let lastSentAt: number | null = null;
    let trailingScheduled = false;

    const sendNow = () => {
        lastSentAt = timers.now();
        send();
    };

    return () => {
        const at = timers.now();
        if (lastSentAt === null || at - lastSentAt >= intervalMs) {
            sendNow();
            return;
        }

        if (trailingScheduled) return;
        trailingScheduled = true;
        timers.setTimeout(() => {
            trailingScheduled = false;
            sendNow();
        }, lastSentAt + intervalMs - at);
    };
}
